import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import {
  fetchPortalContacts,
  addPortalContact,
  updatePortalContact,
  deletePortalContact,
} from "../services/api";
import "../styles/StaffAccessPage.css";

const emptyForm = {
  contactName: "",
  designation: "Staff",
  mobile: "",
  receiveWhatsapp: true,
  canPortalLogin: true,
  canViewOrders: true,
  canViewAccount: false,
  canViewStock: true,
  canViewPrices: false,
};

function StaffAccessPage() {
  const navigate = useNavigate();
  const [owner, setOwner] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await fetchPortalContacts();
      setOwner(response.owner || null);
      setContacts(response.contacts || []);
    } catch (err) {
      setError(err.message || "Unable to load staff access.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setError("");
  };

  const editContact = (contact) => {
    setEditingId(contact.id);
    setForm({
      contactName: contact.contactName || "",
      designation: contact.designation || "Staff",
      mobile: contact.mobile || "",
      receiveWhatsapp: contact.receiveWhatsapp !== false,
      canPortalLogin: contact.canPortalLogin === true,
      canViewOrders: contact.canViewOrders !== false,
      canViewAccount: contact.canViewAccount === true,
      canViewStock: contact.canViewStock !== false,
      canViewPrices: contact.canViewPrices === true,
    });
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();

    if (!form.contactName.trim() || !form.mobile.trim()) {
      setError("Enter staff name and mobile number.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      if (editingId) {
        await updatePortalContact(editingId, form);
      } else {
        await addPortalContact(form);
      }

      resetForm();
      await load();
    } catch (err) {
      setError(err.message || "Unable to save staff access.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (contact) => {
    const ok = window.confirm(
      `Remove ${contact.contactName} from staff access? They will no longer be able to log in with this mobile.`
    );
    if (!ok) return;

    try {
      setSaving(true);
      setError("");
      await deletePortalContact(contact.id);
      if (editingId === contact.id) resetForm();
      await load();
    } catch (err) {
      setError(err.message || "Unable to remove staff access.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="staff-access-page">
      <PageHeader title="Manage Staff Access" showBack onBack={() => navigate("/dashboard")} />

      <main className="staff-access-content">
        <section className="staff-info-card">
          <h2>Main Customer Mobile</h2>
          <p className="staff-owner-name">{owner?.contactName || "Account Owner"}</p>
          <p className="staff-owner-mobile">{owner?.mobileDisplay || owner?.mobile || "—"}</p>
          <div className="staff-owner-badges">
            <span>OTP Login</span>
            <span>Password Login</span>
            <span>Account Owner</span>
          </div>
          <p className="staff-note">
            Only this main registered mobile can add or remove staff access.
          </p>
        </section>

        <section className="staff-form-card">
          <h2>{editingId ? "Edit Staff Member" : "Add Staff Member"}</h2>

          <form onSubmit={save} className="staff-form">
            <div className="staff-field-grid">
              <label>
                <span>Name</span>
                <input
                  type="text"
                  value={form.contactName}
                  onChange={(e) => setForm((v) => ({ ...v, contactName: e.target.value }))}
                  placeholder="Staff name"
                />
              </label>

              <label>
                <span>Designation</span>
                <input
                  type="text"
                  value={form.designation}
                  onChange={(e) => setForm((v) => ({ ...v, designation: e.target.value }))}
                  placeholder="e.g. Purchase, Accounts, Sales"
                />
              </label>

              <label className="staff-field-full">
                <span>Mobile Number</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={form.mobile}
                  onChange={(e) => setForm((v) => ({ ...v, mobile: e.target.value }))}
                  placeholder="10-digit mobile number"
                />
              </label>
            </div>

            <label className="staff-toggle-row">
              <input
                type="checkbox"
                checked={form.receiveWhatsapp}
                onChange={(e) => setForm((v) => ({ ...v, receiveWhatsapp: e.target.checked }))}
              />
              <span>
                <strong>Receive WhatsApp updates</strong>
                <small>Use this number for ABN order-status and other customer updates.</small>
              </span>
            </label>

            <label className="staff-toggle-row">
              <input
                type="checkbox"
                checked={form.canPortalLogin}
                onChange={(e) =>
                  setForm((v) => ({
                    ...v,
                    canPortalLogin: e.target.checked,
                  }))
                }
              />
              <span>
                <strong>Allow Party Portal login</strong>
                <small>This staff member can sign in using OTP sent to this mobile.</small>
              </span>
            </label>

            {form.canPortalLogin && (
              <div className="staff-permission-panel">
                <p className="staff-permission-title">Portal Visibility</p>

                <label className="staff-toggle-row">
                  <input
                    type="checkbox"
                    checked={form.canViewOrders}
                    onChange={(e) =>
                      setForm((v) => ({ ...v, canViewOrders: e.target.checked }))
                    }
                  />
                  <span>
                    <strong>View Orders</strong>
                    <small>Allow access to pending, issued and dispatched order information.</small>
                  </span>
                </label>

                <label className="staff-toggle-row">
                  <input
                    type="checkbox"
                    checked={form.canViewAccount}
                    onChange={(e) =>
                      setForm((v) => ({ ...v, canViewAccount: e.target.checked }))
                    }
                  />
                  <span>
                    <strong>View Account Information</strong>
                    <small>Allow account summary, outstanding and ledger access.</small>
                  </span>
                </label>

                <label className="staff-toggle-row">
                  <input
                    type="checkbox"
                    checked={form.canViewStock}
                    onChange={(e) =>
                      setForm((v) => ({
                        ...v,
                        canViewStock: e.target.checked,
                        canViewPrices: e.target.checked ? v.canViewPrices : false,
                      }))
                    }
                  />
                  <span>
                    <strong>View Stock</strong>
                    <small>Allow stock inquiry and availability checks.</small>
                  </span>
                </label>

                <label className="staff-toggle-row">
                  <input
                    type="checkbox"
                    checked={form.canViewPrices}
                    disabled={!form.canViewStock}
                    onChange={(e) =>
                      setForm((v) => ({ ...v, canViewPrices: e.target.checked }))
                    }
                  />
                  <span>
                    <strong>View Prices</strong>
                    <small>Show dealer-specific cut price inside Stock Inquiry.</small>
                  </span>
                </label>
              </div>
            )}

            {error && <p className="staff-error">{error}</p>}

            <div className="staff-form-actions">
              <button type="submit" className="staff-primary-button" disabled={saving}>
                {saving ? "Saving..." : editingId ? "Update Staff" : "Add Staff"}
              </button>
              {editingId && (
                <button type="button" className="staff-secondary-button" onClick={resetForm}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </section>

        <section className="staff-list-card">
          <div className="staff-list-heading">
            <h2>Authorized Staff</h2>
            <span>{contacts.length}</span>
          </div>

          {loading ? (
            <p className="staff-empty">Loading...</p>
          ) : contacts.length === 0 ? (
            <p className="staff-empty">No staff mobile numbers have been added yet.</p>
          ) : (
            <div className="staff-list">
              {contacts.map((contact) => (
                <div key={contact.id} className="staff-list-item">
                  <div className="staff-list-main">
                    <div>
                      <p className="staff-list-name">{contact.contactName}</p>
                      <p className="staff-list-meta">
                        {contact.designation || "Staff"} · {contact.mobileDisplay || contact.mobile}
                      </p>
                    </div>
                    <div className="staff-statuses">
                      <span className={contact.receiveWhatsapp ? "on" : "off"}>
                        WhatsApp {contact.receiveWhatsapp ? "On" : "Off"}
                      </span>
                      <span className={contact.canPortalLogin ? "on" : "off"}>
                        Portal {contact.canPortalLogin ? "On" : "Off"}
                      </span>
                      {contact.canPortalLogin && (
                        <>
                          <span className={contact.canViewOrders ? "on" : "off"}>
                            Orders {contact.canViewOrders ? "On" : "Off"}
                          </span>
                          <span className={contact.canViewAccount ? "on" : "off"}>
                            Account {contact.canViewAccount ? "On" : "Off"}
                          </span>
                          <span className={contact.canViewStock ? "on" : "off"}>
                            Stock {contact.canViewStock ? "On" : "Off"}
                          </span>
                          <span className={contact.canViewPrices ? "on" : "off"}>
                            Prices {contact.canViewPrices ? "On" : "Off"}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="staff-row-actions">
                    <button type="button" onClick={() => editContact(contact)}>
                      Edit
                    </button>
                    <button type="button" className="danger" onClick={() => remove(contact)}>
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default StaffAccessPage;
